import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePatientReportBatchNotify } from '../../context/PatientReportBatchNotifyContext';
import { getEffectiveTenantScope, getPatients } from '../../services/patientService';
import { getClients } from '../../services/clientService';
import { getClientSites } from '../../services/clientSiteService';
import {
  REVIEW_STATUS,
  getBillingPeriod,
  getSnapshotWithRows,
} from '../../services/billingReviewService';
import {
  getCareUserPreference,
  saveCareUserPreference,
} from '../../services/careUserPreferencesService';
import {
  filterPatientsByScope,
  getDefaultScopeForRole,
  getScopeFilterOptions,
  isScopeValidForRole,
  SCOPE_ALL,
} from '../../utils/patientAccessScope';
import { getUsersForCareAppList } from '../../services/careTenantQueries';
import CollapsibleFilter from '../CollapsibleFilter';
import { buildPeriod, capitalizeTenant, currentYmKey } from '../../utils/reportFormat';
import { clientWideNavFor, filterPolicyForPath, internalReportsFor } from './clientWideReports';
import {
  ALL_CLIENTS_KEY,
  getCurrentReportSnapshot,
  isSnapshotOutdated,
} from '../../services/reportSnapshotService';
import '../../pages/Dashboard.css';
import './ReportsLayout.css';

const STATUS_ACTIVE = 'active';
const STATUS_INACTIVE = 'inactive';
const STATUS_ALL = 'all';

const STATUS_OPTIONS = [
  { value: STATUS_ACTIVE, label: 'Active patients' },
  { value: STATUS_INACTIVE, label: 'Inactive patients' },
  { value: STATUS_ALL, label: 'All patients' },
];

const PERIOD_OPTIONS = [
  { value: 'thisMonth', label: 'Current month' },
  { value: 'lastMonth', label: 'Previous month' },
  { value: 'custom', label: 'Custom range' },
];

const BILLING_FILTER_OPTIONS = [
  { value: 'any', label: 'All patients' },
  { value: 'approved', label: 'Billing-approved only' },
];

const YM_RE = /^\d{4}-\d{2}$/;
const STATUS_VALUES = new Set(STATUS_OPTIONS.map((o) => o.value));
const PERIOD_VALUES = new Set(PERIOD_OPTIONS.map((o) => o.value));

// Persist the global filters for the browser session so selections survive the
// print dialog / any remount (e.g. an auth refresh when the window regains focus).
const FILTERS_KEY = 'reports.filters.v1';

function readStoredFilters() {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function ReportsLayout() {
  const location = useLocation();
  const { user, profile, permissions, loading: authLoading } = useAuth();
  const { unseenCount: batchUnseenCount } = usePatientReportBatchNotify();
  const stored = useMemo(readStoredFilters, []);
  const [scope, setScope] = useState(stored.scope || SCOPE_ALL);
  const [status, setStatus] = useState(stored.status || STATUS_ALL);
  const [patientLoadError, setPatientLoadError] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [scopeUsers, setScopeUsers] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(stored.selectedClientId || '');
  const [clients, setClients] = useState([]);
  const [clientSites, setClientSites] = useState([]);

  // Global, month-granular period shared by every report.
  const [periodPreset, setPeriodPreset] = useState(stored.periodPreset || 'thisMonth');
  const [fromYm, setFromYm] = useState(() => stored.fromYm || currentYmKey());
  const [toYm, setToYm] = useState(() => stored.toYm || currentYmKey());

  // Billing-approved cohort filter (patients confirmed "Bill" in the period).
  const [billingFilter, setBillingFilter] = useState(stored.billingFilter === 'approved' ? 'approved' : 'any');
  const [approvedIds, setApprovedIds] = useState(null); // Set<patientId> | null while loading
  const [billingPeriodsByYm, setBillingPeriodsByYm] = useState(() => new Map());

  // Once the user touches a filter here, their fresh choice must not be
  // clobbered by the (async) restore of the server-persisted preferences.
  const filtersTouchedRef = useRef(false);
  const touch = useCallback((setter) => (v) => {
    filtersTouchedRef.current = true;
    setter(v);
  }, []);

  // Restore server-persisted filters (cross-session / cross-device).
  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;
    getCareUserPreference('reportsFilters').then((saved) => {
      if (cancelled || filtersTouchedRef.current || !saved || typeof saved !== 'object') return;
      if (isScopeValidForRole(saved.scope, profile?.role)) setScope(saved.scope);
      if (STATUS_VALUES.has(saved.status)) setStatus(saved.status);
      if (typeof saved.selectedClientId === 'string') setSelectedClientId(saved.selectedClientId);
      if (PERIOD_VALUES.has(saved.periodPreset)) setPeriodPreset(saved.periodPreset);
      if (YM_RE.test(saved.fromYm || '')) setFromYm(saved.fromYm);
      if (YM_RE.test(saved.toYm || '')) setToYm(saved.toYm);
      if (saved.billingFilter === 'approved' || saved.billingFilter === 'any') setBillingFilter(saved.billingFilter);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.role, profile?.tenantId, profile?.impersonatingTenantId]);

  useEffect(() => {
    const snapshotValue = { scope, status, selectedClientId, periodPreset, fromYm, toYm, billingFilter };
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(snapshotValue));
    } catch {
      /* ignore quota / unavailable storage */
    }
    // Persist to the user's preferences doc only after a deliberate change
    // (never on mount, which would overwrite the stored value with defaults).
    if (!filtersTouchedRef.current) return undefined;
    const timer = setTimeout(() => saveCareUserPreference('reportsFilters', snapshotValue), 800);
    return () => clearTimeout(timer);
  }, [scope, status, selectedClientId, periodPreset, fromYm, toYm, billingFilter]);

  const tenantScope = useMemo(() => getEffectiveTenantScope(profile), [profile]);

  const tenantLabel = useMemo(
    () => capitalizeTenant(tenantScope?.tenantName || profile?.tenantName || tenantScope?.tenantId || ''),
    [tenantScope, profile],
  );

  const period = useMemo(
    () => buildPeriod(periodPreset, fromYm, toYm),
    [periodPreset, fromYm, toYm],
  );

  useEffect(() => {
    if (!profile?.role) return;
    // Keep a restored scope if it's valid for this role; otherwise fall back to the default.
    setScope((cur) => (isScopeValidForRole(cur, profile.role) ? cur : getDefaultScopeForRole(profile.role)));
  }, [profile?.role]);

  useEffect(() => {
    if (profile?.role !== 'supervisor') {
      setScopeUsers([]);
      return;
    }
    let cancelled = false;
    getUsersForCareAppList()
      .then((rows) => {
        if (!cancelled) setScopeUsers(rows);
      })
      .catch(() => {
        if (!cancelled) setScopeUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.role]);

  const refreshPatientsAndMeta = useCallback(async () => {
    if (!profile?.role || tenantScope.mode !== 'tenant') {
      setPatients([]);
      setClients([]);
      setClientSites([]);
      setPatientsLoading(false);
      setPatientLoadError('');
      return;
    }
    setPatientsLoading(true);
    setPatientLoadError('');
    try {
      const [pList, cList, sList] = await Promise.all([
        getPatients(),
        getClients().catch(() => []),
        getClientSites().catch(() => []),
      ]);
      setPatients(pList);
      setClients(cList);
      setClientSites(sList);
    } catch (e) {
      setPatientLoadError(e?.message || 'Failed to load patients.');
      setPatients([]);
      setClients([]);
      setClientSites([]);
    } finally {
      setPatientsLoading(false);
    }
  }, [profile?.role, tenantScope.mode]);

  useEffect(() => {
    if (authLoading) return;
    refreshPatientsAndMeta();
  }, [authLoading, refreshPatientsAndMeta, tenantScope.tenantId]);

  // Reports whose cohort comes from as-of-period rules hide (and ignore) the
  // current-status and billing filters; the saved preference is left untouched.
  const filterPolicy = useMemo(() => filterPolicyForPath(location.pathname), [location.pathname]);
  const effectiveStatus = filterPolicy.patients ? status : STATUS_ALL;
  const effectiveBillingFilter = filterPolicy.billing ? billingFilter : 'any';

  const baseFilteredPatients = useMemo(() => {
    if (!profile?.role) return [];
    let list = filterPatientsByScope(patients, scope, {
      role: profile.role,
      uid: user?.uid,
      users: scopeUsers,
      team: 'rpm',
    });
    if (effectiveStatus === STATUS_ACTIVE) {
      list = list.filter((p) => p.active !== false);
    } else if (effectiveStatus === STATUS_INACTIVE) {
      list = list.filter((p) => p.active === false);
    }
    if (!selectedClientId) return list;
    return list.filter((p) => {
      const site = clientSites.find((s) => s.id === p.clientSiteId);
      return site && String(site.clientId || '') === String(selectedClientId);
    });
  }, [patients, scope, effectiveStatus, profile?.role, user?.uid, scopeUsers, selectedClientId, clientSites]);

  const ymKeys = period?.ymKeys ?? [];
  const ymKeysFingerprint = ymKeys.join('|');
  const baseIdsFingerprint = useMemo(
    () => baseFilteredPatients.map((p) => p.id).join('|'),
    [baseFilteredPatients],
  );

  // Billing period lifecycle for the selected months (drives finalization notes).
  useEffect(() => {
    if (tenantScope.mode !== 'tenant' || !ymKeys.length) {
      setBillingPeriodsByYm(new Map());
      return undefined;
    }
    let cancelled = false;
    Promise.all(ymKeys.map((ym) => getBillingPeriod(ym).then((p) => [ym, p])))
      .then((entries) => {
        if (!cancelled) setBillingPeriodsByYm(new Map(entries));
      })
      .catch(() => {
        if (!cancelled) setBillingPeriodsByYm(new Map());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantScope.mode, tenantScope.tenantId, ymKeysFingerprint]);

  const unfinalizedYms = useMemo(
    () => ymKeys.filter((ym) => billingPeriodsByYm.get(ym)?.status !== 'finalized'),
    [ymKeys, billingPeriodsByYm],
  );

  // Billing-approved is a finalized concept; never construct it from a partial
  // set of live human decisions while a period is still open.
  const billingFilterActive = effectiveBillingFilter === 'approved' && unfinalizedYms.length === 0;
  const billingFilterOptions = useMemo(
    () => BILLING_FILTER_OPTIONS.map((option) => option.value === 'approved'
      ? { ...option, disabled: unfinalizedYms.length > 0 }
      : option),
    [unfinalizedYms.length],
  );
  useEffect(() => {
    if (billingFilter === 'approved' && unfinalizedYms.length > 0) setBillingFilter('any');
  }, [billingFilter, unfinalizedYms.length]);

  useEffect(() => {
    if (!billingFilterActive || tenantScope.mode !== 'tenant' || !ymKeys.length || patientsLoading) {
      setApprovedIds(null);
      return undefined;
    }
    let cancelled = false;
    setApprovedIds(null);
    (async () => {
      const ids = baseFilteredPatients.map((p) => p.id);
      const allowed = new Set(ids);
      const approved = new Set();
      for (const ym of ymKeys) {
        const version = billingPeriodsByYm.get(ym)?.currentSnapshotVersion;
        if (!version) continue;
        // eslint-disable-next-line no-await-in-loop
        const snapshot = await getSnapshotWithRows(version);
        (snapshot?.rows || []).forEach((row) => {
          if (allowed.has(row.patientId) && row.billingStatus === REVIEW_STATUS.BILL) approved.add(row.patientId);
        });
      }
      if (!cancelled) setApprovedIds(approved);
    })().catch(() => {
      if (!cancelled) setApprovedIds(new Set());
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingFilterActive, tenantScope.mode, tenantScope.tenantId, ymKeysFingerprint, baseIdsFingerprint, patientsLoading, billingPeriodsByYm]);

  const billingFilterLoading = billingFilterActive && approvedIds === null;

  const scopeFilteredPatients = useMemo(() => {
    if (!billingFilterActive) return baseFilteredPatients;
    if (approvedIds === null) return [];
    return baseFilteredPatients.filter((p) => approvedIds.has(p.id));
  }, [billingFilterActive, baseFilteredPatients, approvedIds]);

  const patientIds = useMemo(() => scopeFilteredPatients.map((p) => p.id), [scopeFilteredPatients]);

  const scopeOptions = useMemo(() => getScopeFilterOptions(profile?.role), [profile?.role]);

  const clientOptions = useMemo(() => {
    const opts = [...clients]
      .filter((c) => (c.status || 'active').toLowerCase() !== 'inactive')
      .sort((a, b) =>
        String(a.clientName || '').localeCompare(String(b.clientName || ''), undefined, { sensitivity: 'base' }),
      )
      .map((c) => ({ value: c.id, label: c.clientName || c.clientShortName || c.id }));
    return [{ value: '', label: 'All clients' }, ...opts];
  }, [clients]);

  // Drop a restored client selection that no longer exists once clients have loaded.
  useEffect(() => {
    if (patientsLoading || !clients.length) return;
    if (selectedClientId && !clients.some((c) => c.id === selectedClientId)) {
      setSelectedClientId('');
    }
  }, [patientsLoading, clients, selectedClientId]);

  // Warn the operator before exporting a partial client report. Always-printable
  // reports should be run with full scope + all statuses so totals aren't cut.
  const cohortNarrowed = scope !== SCOPE_ALL || effectiveStatus !== STATUS_ALL;

  // Frozen report snapshot for a single-month period (complete-scope runs only).
  const snapshotYm = ymKeys.length === 1 ? ymKeys[0] : '';
  const snapshotEligible = Boolean(snapshotYm) && scope === SCOPE_ALL;
  const [snapshotState, setSnapshotState] = useState({ ym: '', snapshot: null, loading: false });
  useEffect(() => {
    if (tenantScope.mode !== 'tenant' || !snapshotYm) {
      setSnapshotState({ ym: '', snapshot: null, loading: false });
      return undefined;
    }
    let cancelled = false;
    setSnapshotState({ ym: snapshotYm, snapshot: null, loading: true });
    getCurrentReportSnapshot(snapshotYm, tenantScope.tenantId)
      .then((snapshot) => {
        if (!cancelled) setSnapshotState({ ym: snapshotYm, snapshot, loading: false });
      })
      .catch(() => {
        if (!cancelled) setSnapshotState({ ym: snapshotYm, snapshot: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantScope.mode, tenantScope.tenantId, snapshotYm]);

  const reportSnapshot = useMemo(() => {
    const ready = snapshotState.ym === snapshotYm && !snapshotState.loading;
    const billingPeriod = billingPeriodsByYm.get(snapshotYm) || null;
    return {
      ym: snapshotYm,
      eligible: snapshotEligible,
      loading: !ready,
      snapshot: ready ? snapshotState.snapshot : null,
      outdated: ready && snapshotState.snapshot ? isSnapshotOutdated(snapshotState.snapshot, billingPeriod) : false,
      billingFinalized: billingPeriod?.status === 'finalized',
      clientKey: selectedClientId || ALL_CLIENTS_KEY,
    };
  }, [snapshotState, snapshotYm, snapshotEligible, billingPeriodsByYm, selectedClientId]);

  const outletContext = useMemo(
    () => ({
      tenantScope,
      tenantLabel,
      patientsLoading,
      patientLoadError,
      scopedPatients: scopeFilteredPatients,
      patientIds,
      refreshPatients: refreshPatientsAndMeta,
      selectedClientId,
      clients,
      clientSites,
      period,
      cohortNarrowed,
      scope,
      status: effectiveStatus,
      billingFilter: effectiveBillingFilter,
      billingPeriodsByYm,
      unfinalizedYms,
      filterPolicy,
      reportSnapshot,
    }),
    [
      tenantScope,
      tenantLabel,
      patientsLoading,
      patientLoadError,
      scopeFilteredPatients,
      patientIds,
      refreshPatientsAndMeta,
      selectedClientId,
      clients,
      clientSites,
      period,
      cohortNarrowed,
      scope,
      effectiveStatus,
      effectiveBillingFilter,
      billingPeriodsByYm,
      unfinalizedYms,
      filterPolicy,
      reportSnapshot,
    ],
  );

  if (authLoading) {
    return (
      <div className="reports-layout reports-layout-loading">
        <p>Loading…</p>
      </div>
    );
  }

  if (tenantScope.mode === 'none') {
    return (
      <div className="dashboard">
        <h1>Reports</h1>
        <p className="reports-tenant-hint">
          Your account has no <strong>tenant</strong> assigned. Ask an administrator to set <code>tenantId</code> on your
          user profile.
        </p>
      </div>
    );
  }

  if (tenantScope.mode !== 'tenant') {
    return (
      <div className="dashboard">
        <h1>Reports</h1>
        <p className="reports-tenant-hint">
          Select a tenant using <strong>superuser impersonation</strong> in the header to run reports for that
          organization.
        </p>
      </div>
    );
  }

  return (
    <div className={`reports-layout${location.pathname === '/reports/billing-review' ? ' reports-layout--billing-review' : ''}`}>
      <aside className="reports-sidebar" aria-label="Report navigation">
        <h2 className="reports-sidebar-title">Reports</h2>
        <nav className="reports-nav">
          <NavLink end className="reports-nav-link reports-nav-overview" to="/reports">
            Overview
          </NavLink>
          <NavLink end className="reports-nav-link" to="/reports/how-reports-work">
            How reports work
          </NavLink>
          <div className="reports-nav-group" data-section="tools">
            <p className="reports-nav-section-label">Tools</p>
            {permissions?.canManageBillingReview ? (
              <NavLink className="reports-nav-link" to="/reports/billing-review">
                Billing review
              </NavLink>
            ) : null}
            <NavLink className="reports-nav-link" to="/reports/package">
              Client report package
            </NavLink>
            <NavLink className="reports-nav-link" to="/reports/patient-batch">
              Patient report batch
              {batchUnseenCount ? (
                <span className="reports-nav-badge" aria-label={`${batchUnseenCount} new finished run(s)`}>
                  {batchUnseenCount}
                </span>
              ) : null}
            </NavLink>
            {permissions?.canSeeTenantAdminSettings ? (
              <NavLink className="reports-nav-link" to="/admin/report-settings">
                Report settings
              </NavLink>
            ) : null}
          </div>
          <div className="reports-nav-group" data-section="individual">
            <p className="reports-nav-section-label">Individual patient reports</p>
            <NavLink className="reports-nav-link" to="/reports/patient-summary">
              Patient summary
            </NavLink>
          </div>
          <div className="reports-nav-group" data-section="client">
            <p className="reports-nav-section-label">Client-wide reports</p>
            {clientWideNavFor(permissions).map((r) => (
              <NavLink key={r.to} end className="reports-nav-link" to={r.to}>
                {r.title}
              </NavLink>
            ))}
          </div>
          {internalReportsFor(permissions).length ? (
            <div className="reports-nav-group" data-section="internal">
              <p className="reports-nav-section-label">Internal reports</p>
              {internalReportsFor(permissions).map((r) => (
                <NavLink key={r.to} end className="reports-nav-link" to={r.to}>
                  {r.title}
                </NavLink>
              ))}
            </div>
          ) : null}
        </nav>
      </aside>
      <div className="reports-layout-body">
        <section className="reports-global-filters dashboard-section no-print">
          <div className="reports-filter-row">
            {scopeOptions ? (
              <CollapsibleFilter
                label="Scope"
                ariaLabel="Patient scope"
                value={scope}
                options={scopeOptions}
                onChange={touch(setScope)}
              />
            ) : null}
            {filterPolicy.patients ? (
              <CollapsibleFilter
                label="Patients"
                ariaLabel="Patient status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={touch(setStatus)}
              />
            ) : null}
            <CollapsibleFilter
              label="Client"
              ariaLabel="Client"
              value={selectedClientId}
              options={clientOptions}
              onChange={touch(setSelectedClientId)}
            />
            <CollapsibleFilter
              label="Period"
              ariaLabel="Reporting period"
              value={periodPreset}
              options={PERIOD_OPTIONS}
              onChange={touch(setPeriodPreset)}
            />
            {periodPreset === 'custom' ? (
              <span className="reports-period-months">
                <label className="reports-period-month">
                  From
                  <input
                    type="month"
                    value={fromYm}
                    max={toYm}
                    onChange={(e) => touch(setFromYm)(e.target.value)}
                  />
                </label>
                <label className="reports-period-month">
                  To
                  <input
                    type="month"
                    value={toYm}
                    min={fromYm}
                    onChange={(e) => touch(setToYm)(e.target.value)}
                  />
                </label>
              </span>
            ) : null}
            {filterPolicy.billing ? (
              <CollapsibleFilter
                label="Billing"
                ariaLabel="Billing approval filter"
                value={billingFilter}
                options={billingFilterOptions}
                onChange={touch(setBillingFilter)}
              />
            ) : null}
            <span className="reports-scope-count">
              {patientsLoading
                ? 'Loading scope…'
                : billingFilterLoading
                  ? 'Checking billing decisions…'
                  : `${patientIds.length} patient(s) · ${period.periodLabel}`}
            </span>
            {patientLoadError ? <span className="dashboard-error">{patientLoadError}</span> : null}
          </div>
          {unfinalizedYms.length && !patientsLoading ? (
            <p className="reports-billing-finalization-note">
              Billing review not finalized for {unfinalizedYms.join(', ')} — billing-related figures may still change.
            </p>
          ) : null}
          {billingFilterActive && !billingFilterLoading ? (
            <p className="reports-billing-finalization-note">
              Billing-approved filter: showing patients marked <strong>Bill</strong> in a finalized snapshot for at least one
              month of the period.
            </p>
          ) : null}
          {!filterPolicy.patients || !filterPolicy.billing ? (
            <p className="reports-billing-finalization-note">
              Patients are chosen by this report&apos;s own as-of-period rules, so the Patients and Billing filters
              don&apos;t apply here. <NavLink to="/reports/how-reports-work">How reports work</NavLink>
            </p>
          ) : null}
          {cohortNarrowed ? (
            <p className="reports-cohort-warning">
              Heads up: this report is scoped to{' '}
              <strong>{scope === SCOPE_ALL ? 'all patients' : 'a subset of patients'}</strong>
              {effectiveStatus !== STATUS_ALL ? (
                <>
                  {' '}and only <strong>{effectiveStatus}</strong> patients
                </>
              ) : null}
              .{' '}
              {filterPolicy.patients ? (
                <>
                  For a complete client-facing report, set <strong>Scope = All Patients</strong> and{' '}
                  <strong>Patients = All patients</strong> so totals aren&apos;t undercounted.
                </>
              ) : (
                <>
                  For a complete client-facing report, set <strong>Scope = All Patients</strong>. Frozen snapshots
                  are only used with the full scope.
                </>
              )}
            </p>
          ) : null}
        </section>
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
